/* eslint-disable max-lines */
import {endOfDay} from 'date-fns'
import {observer} from 'mobx-react-lite'
import React, {useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {
  TaskType,
  TaskWithColor,
  useCalendarStore
} from '@components/calendar/calendar-store'
import {validDateFormat} from '@components/calendar/utils'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {noPropagate} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {DraggableDeadline} from '../draggable/draggable-deadline'
import {DraggableAllDayDeadline} from '../draggable/draggable-deadline-all-day'
import {useDraggableStore} from '../draggable/draggable-store'
import {mouseDown, mouseMove, mouseUp} from './task-content-utils'
import {TaskText} from './task-text'

const TaskContentWrapperTw = tw.div<{
  $wFull: boolean
  $allDay: boolean
  $changeDeadline?: boolean
  $eventsNone: boolean
}>`
  ${p => (p.$wFull ? 'w-full' : 'right-10 min-w-32')}
  ${p => p.$allDay && 'flex'}
  ${p => p.$changeDeadline && 'z-20'}
  ${p => p.$eventsNone && 'pointer-events-none'}
  left-0
  absolute
  shadow-task-old
  rounded-4
`

const TaskContentTw = tw.div<{
  $dragShowed: boolean
  $isTaskInPast: boolean
  $whiteBb?: boolean
}>`
  ${p => (p.$whiteBb ? 'bg-on-color' : '')}
  ${p => (p.$isTaskInPast ? 'opacity-65' : 'opacity-100')}
  ${p =>
    p.$dragShowed
      ? 'border-b-0 rounded-br-0 rounded-bl-0 rounded-tr-4 rounded-tl-4'
      : 'rounded-4'}
  w-full
  pl-8
  pr-2
  border
  border-solid
  border-bg-main-old
  justify-between
  left-0
  absolute
  leading-5
  cursor-pointer
  hover:z-20
`

const ActiveTaskBar = tw.div<{
  $dragShowed: boolean
}>`
  ${p => (p.$dragShowed ? 'rounded-bl-0' : 'rounded-bl-4')}
  absolute
  top-0
  bottom-0
  left-0
  w-4
  bg-accent-old
  rounded-tl-4
`

export const TimeTw = tw.div`
  leading-4
  text-11
  text-main-old
  opacity-60
  truncate
`

export const BottomDeadline = tw.div`
  absolute
  bottom-4
  left-8
  text-main-old
  text-11
  opacity-60
`

interface TaskContentProps {
  task: TaskWithColor
  customTop?: number
  textOpacity?: string
  allDay: boolean
  wFull?: boolean
  className?: string
  top?: number
  bottom?: number
  height: number
  drag?: boolean
  allDayDrag?: boolean
  allDayDragWithoutStartDate?: boolean
  allDayWidth?: number
  allDayTop?: number
  dragWithoutStartDate?: boolean
  type: TaskType
  showBottomDeadline?: boolean
  hideDeadline?: boolean
  timeWithDate?: boolean
  changeDeadlineExpansion?: boolean
}

export const TaskContent = observer(
  // eslint-disable-next-line max-lines-per-function
  ({
    task,
    customTop,
    textOpacity,
    allDay = false,
    wFull = false,
    drag = false,
    allDayDrag = false,
    allDayDragWithoutStartDate = false,
    allDayWidth,
    allDayTop,
    dragWithoutStartDate = false,
    showBottomDeadline = false,
    hideDeadline = false,
    timeWithDate = false,
    changeDeadlineExpansion = false,
    className = '',
    top,
    bottom,
    height,
    type
  }: TaskContentProps) => {
    const ref = useRef<HTMLElement>()
    const [state, store] = useCalendarStore()
    const [draggableState, draggableStore] = useDraggableStore()

    const onMouseDown = (e: React.MouseEvent) => {
      noPropagate(e)
      mouseDown(draggableStore, {
        e,
        store,
        task,
        type,
        allDay,
        ref,
        height,
        allDayWidth,
        allDayTop
      })
      document.addEventListener('mouseup', onMouseUp)
      document.addEventListener('mousemove', onMouseMove)
    }

    const onMouseMove = (e: MouseEvent) => {
      e.stopPropagation()
      mouseMove(draggableState, draggableStore, {
        e,
        state,
        task,
        type,
        height,
        allDayWidth,
        allDayTop,
        dragWithoutStartDate
      })
    }

    const onMouseUp = (e: MouseEvent) => {
      e.stopPropagation()
      mouseUp(draggableState, draggableStore, {
        e,
        state,
        store,
        task,
        allDay,
        dragWithoutStartDate
      })
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('mousemove', onMouseMove)
    }

    const isTaskInPast = allDay
      ? new Date(state.currentTime) > endOfDay(task.roundedDeadline)
      : task.roundedDeadline < new Date(state.currentTime)
    const taskDraggedId = draggableState.draggedItem.id === task.task.id
    const taskHoveredId = draggableState.hoveredItemId === task.task.id
    const taskTop = customTop ?? top
    const taskBottom = bottom
    const taskHeight = customTop ? height + Math.abs(customTop) : height

    const taskMarginLeft =
      type === 'with-start-date' &&
      `${
        task.marginShiftLeft > 11
          ? 158
          : task.marginShiftLeft < 4
          ? task.marginShiftLeft * 24
          : task.marginShiftLeft * 10 + 48
      }px`

    const taskMarginRight =
      type === 'with-start-date' &&
      `${
        task.marginShiftRight > 11
          ? 158
          : task.marginShiftRight < 4
          ? task.marginShiftRight * 24
          : task.marginShiftRight * 10 + 48
      }px`

    const taskOpacity = draggableState.enabled && taskDraggedId && 0
    const taskZIndex =
      draggableState.draggedItem.id &&
      taskDraggedId &&
      draggableState.draggedItem.changeAction === 'change-deadline'
    const dragShowed =
      changeDeadlineExpansion &&
      drag &&
      draggableState.showDrag &&
      taskHoveredId

    return (
      <>
        <TaskContentWrapperTw
          $wFull={wFull}
          $changeDeadline={taskZIndex}
          $eventsNone={draggableState.enabled && !taskDraggedId}
          $allDay={allDay}
          style={{
            top: taskTop,
            bottom: taskBottom,
            height: dragShowed ? taskHeight + 8 : taskHeight,
            marginLeft: taskMarginLeft || 0,
            marginRight: taskMarginRight || 0,
            opacity: taskOpacity || 1,
            zIndex: taskHoveredId ? 20 : undefined
          }}
          onMouseDown={noPropagate}
          onMouseLeave={e => {
            // stop mouseLeave firing after mouseDown
            if (e.buttons !== 1) {
              draggableStore.hoveredItemId(null)
              draggableStore.showedDrag(false)
            }
          }}
          ref={ref}
        >
          {isTaskInPast && (
            <TaskContentTw
              $whiteBb
              $isTaskInPast={isTaskInPast}
              $dragShowed={dragShowed}
              style={{
                height: taskHeight,
                opacity: taskOpacity || 1
              }}
              className={cn(className)}
              onMouseMove={e => {
                noPropagate(e)
                if (draggableState.hoveredItemId !== task.task.id) {
                  draggableStore.hoveredItemId(task.task.id)
                  draggableStore.showedDrag(true)
                }
              }}
              onMouseEnter={e => {
                noPropagate(e)
                store.showedPopupButton(false, null)
              }}
              onMouseDown={onMouseDown}
            />
          )}
          <TaskContentTw
            $isTaskInPast={isTaskInPast}
            $dragShowed={dragShowed}
            style={{
              backgroundColor: `${task.columnColor}${'e6'}`,
              height: taskHeight,
              opacity: taskOpacity || 1
            }}
            className={cn(className)}
            onMouseMove={e => {
              noPropagate(e)
              if (draggableState.hoveredItemId !== task.task.id) {
                draggableStore.hoveredItemId(task.task.id)
                draggableStore.showedDrag(true)
              }
            }}
            onMouseEnter={e => {
              noPropagate(e)
              store.showedPopupButton(false, null)
            }}
            onMouseDown={onMouseDown}
          >
            {rightPanelStore.state.currentChat?.id === task.task.id && (
              <ActiveTaskBar $dragShowed={dragShowed} />
            )}
            <TaskText
              allDay={allDay}
              height={height}
              taskId={task.task.id}
              hideDeadline={hideDeadline}
              realStartDate={+task.task.startDate}
              realDeadline={+task.task.deadline}
              gtdStage={task.task.gtdStage}
              roundedDeadline={task.roundedDeadline}
              columnColor={task.columnColor}
              title={task.task.title}
              isColorDark={task.isColorDark}
              textOpacity={textOpacity}
              assigned={task.task.assigned}
              customTop={customTop}
              timeWithDate={timeWithDate}
              isSubtask={task.isSubtask}
            />
            {showBottomDeadline && height > 45 && (
              <BottomDeadline>
                {Lang.msg.calendar.deadline}:{' '}
                {draggableState.draggedItem.id &&
                taskDraggedId &&
                draggableState.draggedItem.changeAction === 'change-deadline'
                  ? validDateFormat(draggableState.deadline, 'HH:mm')
                  : validDateFormat(
                      Time.toLocalDate(+task.task.deadline),
                      'HH:mm'
                    )}
              </BottomDeadline>
            )}
            {taskHoveredId && draggableState.showDrag && allDayDrag && (
              <DraggableAllDayDeadline
                task={task}
                allDayDragWithoutStartDate={allDayDragWithoutStartDate}
                type={type}
                ref={ref}
              />
            )}
          </TaskContentTw>
          {taskHoveredId && draggableState.showDrag && drag && (
            <div
              className="absolute left-0 w-full right-10 min-w-32"
              style={{
                height: taskHeight,
                opacity: taskOpacity || 1
              }}
              onMouseMove={e => {
                noPropagate(e)
                if (draggableState.hoveredItemId !== task.task.id) {
                  draggableStore.hoveredItemId(task.task.id)
                  draggableStore.showedDrag(true)
                }
              }}
              onMouseEnter={e => {
                noPropagate(e)
                store.showedPopupButton(false, null)
              }}
            >
              <DraggableDeadline
                isTaskInPast={isTaskInPast}
                changeDeadlineExpansion={changeDeadlineExpansion}
                task={task}
                dragWithoutStartDate={dragWithoutStartDate}
                type={type}
                ref={ref}
              />
            </div>
          )}
        </TaskContentWrapperTw>
      </>
    )
  }
)
